import * as React from "react";

import { handleDocumentNavClick } from "~/hec/components/ui/document-nav-guard";
import { InsideLinkContext } from "~/hec/components/ui/inside-link-context";

/**
 * COM-67 — the `linkComponent` for every Builder `<Content>`.
 *
 * Builder renders a block-level `link` field as its OWN anchor, not through
 * `link-render.tsx`:
 *
 *   tagName === "a" || properties?.href || href
 *     ? linkComponent || "a"
 *     : tagName || "div"
 *
 * So without a `linkComponent` those anchors are raw `<a href>` that never
 * receive `handleDocumentNavClick`, and repeat clicks restart the full-document
 * navigation on every click (`ERR_ABORTED` churn ~55-300ms apart) with nothing
 * suppressing them — the exact failure the COM-67 guard was added to stop.
 * Measured 2026-09-03: 12 clicks produced 12 document navigations (11 aborted)
 * on 6/6 trials, against 0-4 for a guarded CTA on the same page.
 *
 * `PrefetchBuilderAnchors` does not cover them either: it bails on
 * `a.host !== window.location.host`, and it only wraps the page body — the
 * chrome surfaces (`ChromeContent`, `VisibleChromeContent`: footer, alert bar,
 * mobile nav drawer, account dropdown) have no delegation at all.
 *
 * This restores the guard for that whole class in one place. It deliberately
 * does NOT change where a link goes or how it navigates — a full-document link
 * stays a full-document link. Only re-entrant clicks while a navigation is
 * already in flight are swallowed.
 *
 * Two ordering details that matter:
 *
 *  - The block's own `onClick` runs FIRST. Builder click actions arrive as an
 *    `onClick` in the block's attributes; clobbering it would break them. And
 *    if an action calls `preventDefault()`, the guard then sees
 *    `defaultPrevented` and bypasses — which is the correct outcome, since no
 *    navigation will start.
 *  - The visual editor's capture-phase link guard also runs before this
 *    (capture precedes bubble), so editing a link in Builder is unaffected for
 *    the same `defaultPrevented` reason.
 *
 * All other bypasses live in `document-nav-guard.ts` (non-primary button,
 * modifier clicks, `target != _self`, `download`), so a Builder link that opens
 * a new tab is never armed and never blocked.
 */
export const GuardedBuilderLink = React.forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement>
>(function GuardedBuilderLink({ onClick, href, children, ...rest }, ref) {
  const insideLink = React.useContext(InsideLinkContext);
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Block-authored handler first — see the ordering note above.
      onClick?.(event);
      handleDocumentNavClick(event);
    },
    [onClick],
  );

  // A block-level link authored INSIDE a Link Wrapper / CTA (seen on the HSA
  // homepage: a linked Text block inside a linked section) would nest `<a>`
  // in `<a>` — invalid HTML that browsers split and React warns about on
  // every render. The outer anchor owns the click; render this one as its
  // text with the block's classes (Tailwind's preflight styles `a` like
  // inherited text, so nothing visible changes).
  if (insideLink) {
    return (
      <span
        ref={ref as unknown as React.Ref<HTMLSpanElement>}
        {...(rest as React.HTMLAttributes<HTMLSpanElement>)}
      >
        {children}
      </span>
    );
  }

  // `href` and `children` are destructured rather than spread so the anchor is
  // statically a native interactive element with content — otherwise
  // jsx-a11y/{anchor-has-content,no-static-element-interactions} fire, since
  // the rule cannot see either arriving through the spread.
  // Children learn they're inside a link, so a nested Builder link or a
  // Heading's rich-text link (COM-884) doesn't render a second anchor.
  return (
    <a ref={ref} href={href} {...rest} onClick={handleClick}>
      <InsideLinkContext value>{children}</InsideLinkContext>
    </a>
  );
});
